// @ts-check

/** Interaction layer for the PLP filter bar and drawer.
 *
 * Filtering itself belongs to the stock facets machinery: every control sits in one
 * `facets-form-component`, and this asks that component to serialize the form and re-render the
 * section, so URL state, history and the `page` reset stay in one place. What is left here is the
 * parts stock has no equivalent for: the two promoted bar dropdowns, the link-style facets, and
 * the drawer's own chrome. */

import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * The stock facets form. `assets/facets.js` does not export its class, so this is the shape
 * relied on rather than the type.
 *
 * @typedef {HTMLElement & {
 *   updateFilters: () => void,
 *   updateFiltersByURL: (url: string) => void,
 * }} FacetsForm
 */

/**
 * @extends {Component}
 */
class PlpFilters extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown);
  }

  /**
   * A control changed: copy it onto its twin in the other surface, then filter.
   * @param {Event} event
   */
  handleChange(event) {
    const node = event.composedPath()[0];
    // The Sort chip's radios share the drawer's group, so checking a twin unchecks the source:
    // only a pick from the bar is copied, since the bar's copies sit out the serialization.
    if (
      node instanceof HTMLInputElement &&
      (node.type === 'checkbox' || (node.type === 'radio' && node.closest('.plp-filters__popover')))
    ) {
      this.#mirror(node);
    }

    this.#filter();
    this.#reflowDrawer();
  }

  /**
   * Close the bar dropdowns on Escape.
   * @param {Event} event
   */
  handleKeydown(event) {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return;
    // Closing hides the option that had focus, so focus goes back to that dropdown's summary.
    const open = this.#dropdowns().find((d) => d.open && d.contains(document.activeElement));
    this.#closeDropdowns();
    const summary = open?.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus();
  }

  /**
   * Keep one bar dropdown open at a time and reflect the state for assistive tech.
   * @param {Event} event
   */
  handleDropdownToggle(event) {
    const details = this.#closest(event, '.plp-filters__dropdown');
    if (!(details instanceof HTMLDetailsElement)) return;

    details
      .querySelector('.plp-filters__dropdown-summary')
      ?.setAttribute('aria-expanded', String(details.open));

    if (!details.open) return;
    for (const other of this.#dropdowns()) {
      if (other !== details) other.open = false;
    }
  }

  /**
   * Category rows and price buckets are real links, so they work with JavaScript off. Here they
   * become a filter update instead, which keeps the shopper's scroll position.
   * @param {Event} event
   */
  handleNavigate(event) {
    if (event instanceof MouseEvent) {
      const opensElsewhere = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (opensElsewhere || event.button !== 0) return;
    }

    const link = this.#closest(event, 'a[href]');
    const form = this.#form;
    if (!(link instanceof HTMLAnchorElement) || !form) return;

    event.preventDefault();
    form.updateFiltersByURL(link.href);
  }

  /**
   * Reset to a server-rendered URL. Which one depends on the control pressed: the drawer's Clear
   * all carries its own `data-clear-url` because it drops the sort as well, and anything without
   * one falls back to the component's, which keeps the sort.
   * @param {Event} event
   */
  clearAll(event) {
    const control = this.#closest(event, '[data-clear-url]');
    const url = control instanceof HTMLElement ? control.dataset.clearUrl : this.dataset.clearUrl;
    const form = this.#form;
    if (!url || !form) return;

    form.updateFiltersByURL(url);
  }

  /**
   * The drawer's footer button is the form's submitter for the no-JS path. With JS the results
   * behind the drawer are already up to date, so the press only closes it.
   * @param {Event} event
   */
  applyAndClose(event) {
    event.preventDefault();

    const dialog = this.querySelector('#plp-filters-drawer');
    if (dialog instanceof DialogComponent) void dialog.closeDialog();
  }

  /**
   * Hand the current form state to the facets component.
   *
   * The bar dropdowns repeat two of the drawer's groups, so the same name and value sit in the
   * form twice. Disabling the copies across the serialization leaves one value per filter, the
   * way stock disables the twin of its sort control (`assets/facets.js`). `updateFilters` reads
   * the form synchronously, before the section request goes out.
   */
  #filter() {
    const form = this.#form;
    if (!form) return;

    const copies = this.#inputs('.plp-filters__popover input');
    for (const copy of copies) copy.disabled = true;
    form.updateFilters();
    for (const copy of copies) copy.disabled = false;
  }

  /**
   * Copy a checkbox or radio onto the same option in the other surface. Comparing the properties
   * beats building an attribute selector: filter names and values carry dots, spaces and quotes.
   * @param {HTMLInputElement} input
   */
  #mirror(input) {
    for (const twin of this.#inputs(`input[type="${input.type}"]`)) {
      if (twin !== input && twin.name === input.name && twin.value === input.value) {
        twin.checked = input.checked;
      }
    }
  }

  /** Chromium skips re-layout of the open drawer when content changes, since the slide-in leaves
   * a transform on the dialog; toggling display in one task forces it without ever painting. */
  #reflowDrawer() {
    const dialog = this.querySelector('.plp-filters__drawer');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const inner = dialog.querySelector('.plp-filters__inner');
    if (!(inner instanceof HTMLElement)) return;

    inner.style.display = 'none';
    void inner.offsetHeight; // flush the collapsed state
    inner.style.display = '';
    void inner.offsetHeight; // flush a fresh re-layout
  }

  /**
   * Close the dropdowns when a pointer goes down outside an open one.
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('.plp-filters__dropdown[open]')) {
      this.#closeDropdowns();
    }
  };

  /**
   * The element a delegated event really started from. Inside a delegated handler `event.target`
   * is rewritten to the bound element (`assets/component.js`), so the path is the only way back.
   * @param {Event} event
   * @param {string} selector
   * @returns {Element | null}
   */
  #closest(event, selector) {
    const node = event.composedPath()[0];
    return node instanceof Element ? node.closest(selector) : null;
  }

  /** @returns {FacetsForm | null} */
  get #form() {
    const form = this.querySelector('facets-form-component');
    if (
      !(form instanceof HTMLElement) ||
      typeof (/** @type {any} */ (form).updateFilters) !== 'function'
    ) {
      return null;
    }
    return /** @type {FacetsForm} */ (form);
  }

  /** @returns {HTMLDetailsElement[]} The bar dropdowns. */
  #dropdowns() {
    return /** @type {HTMLDetailsElement[]} */ ([
      ...this.querySelectorAll('.plp-filters__dropdown'),
    ]);
  }

  #closeDropdowns() {
    for (const details of this.#dropdowns()) details.open = false;
  }

  /**
   * @param {string} selector
   * @returns {HTMLInputElement[]}
   */
  #inputs(selector) {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll(selector)]);
  }
}

if (!customElements.get('plp-filters')) {
  customElements.define('plp-filters', PlpFilters);
}
